import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import * as authService from '../services/auth.service';
import { setAuthToken } from '../services/api.service';
import type { User } from '../services/auth.service';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  signUp: (
    username: string,
    email: string,
    password: string
  ) => Promise<boolean>;
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<boolean>;
  resetPassword: (email: string) => Promise<boolean>;
  confirmResetPassword: (
    email: string,
    code: string,
    newPassword: string
  ) => Promise<boolean>;
  clearError: () => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      signUp: async (username, email, password) => {
        set({ isLoading: true, error: null });
        try {
          const result = await authService.signUp({
            username,
            email,
            password,
          });
          set({ isLoading: false });
          return result.success;
        } catch (err) {
          const error = err instanceof Error ? err.message : 'Sign up failed';
          set({ isLoading: false, error });
          return false;
        }
      },

      signIn: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const result = await authService.signIn({ email, password });
          if (result.success && result.user) {
            // Set auth token for API calls (mock token for now)
            setAuthToken(`mock-token-${result.user.id}`);
            set({
              user: result.user,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          }
          set({ isLoading: false });
          return false;
        } catch (err) {
          const error = err instanceof Error ? err.message : 'Sign in failed';
          set({ isLoading: false, error });
          return false;
        }
      },

      signOut: async () => {
        set({ isLoading: true });
        try {
          await authService.signOut();
        } finally {
          // Clear auth token for API calls
          setAuthToken(null);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      confirmSignUp: async (email, code) => {
        set({ isLoading: true, error: null });
        try {
          const result = await authService.confirmSignUp({ email, code });
          set({ isLoading: false });
          return result.success;
        } catch (err) {
          const error =
            err instanceof Error ? err.message : 'Confirmation failed';
          set({ isLoading: false, error });
          return false;
        }
      },

      resetPassword: async (email) => {
        set({ isLoading: true, error: null });
        try {
          const result = await authService.resetPassword({ email });
          set({ isLoading: false });
          return result.success;
        } catch (err) {
          const error =
            err instanceof Error ? err.message : 'Reset password failed';
          set({ isLoading: false, error });
          return false;
        }
      },

      confirmResetPassword: async (email, code, newPassword) => {
        set({ isLoading: true, error: null });
        try {
          const result = await authService.confirmResetPassword({
            email,
            code,
            newPassword,
          });
          set({ isLoading: false });
          return result.success;
        } catch (err) {
          const error =
            err instanceof Error ? err.message : 'Password reset failed';
          set({ isLoading: false, error });
          return false;
        }
      },

      clearError: () => set({ error: null }),

      checkAuth: async () => {
        set({ isLoading: true });
        try {
          const user = await authService.getCurrentUser();
          set({
            user,
            isAuthenticated: !!user,
            isLoading: false,
          });
        } catch {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // Only persist user data, not loading/error states
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Hook for easy access to auth state
export function useAuth() {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    signUp,
    signIn,
    signOut,
    confirmSignUp,
    resetPassword,
    confirmResetPassword,
    clearError,
    checkAuth,
  } = useAuthStore();

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    signUp,
    signIn,
    signOut,
    confirmSignUp,
    resetPassword,
    confirmResetPassword,
    clearError,
    checkAuth,
  };
}
